/**
 * ThemeContext.jsx
 *
 * Mirrors Hirality HRMS's real ThemeContext.jsx pattern exactly:
 *   1. Injects every T token as a CSS variable on :root (--ats-*) so any
 *      plain CSS/SCSS can use var(--ats-cyan) etc, same as hrms.module.scss
 *      does with --hrms-*.
 *   2. Provides useTheme() -> { theme, T, toggleTheme } for inline JS styles.
 *   3. ThemeToggle -- drop into any header.
 *
 * The one addition beyond the original: since this app's components are
 * MUI-based, ThemeProvider also builds and wraps an MUI theme (via
 * buildMuiTheme in theme.js) from the same T tokens, so MUI components
 * re-color automatically alongside the CSS variables -- no separate MUI
 * dark-mode system to keep in sync.
 *
 * CSS variable prefix is --ats- (not --hrms-) and the localStorage key is
 * "ats_theme" (not "hrms_theme") to avoid collisions if this module ever
 * runs mounted alongside the real Hirality app. Rename both back to
 * hrms-/hrms_theme for full 1:1 parity if/when this merges into the main
 * repo.
 */
import React, { createContext, useContext, useState, useEffect } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { THEMES, buildMuiTheme } from "../theme/theme";

const ThemeContext = createContext({
  theme:       "dark",
  T:           THEMES.dark,
  toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("ats_theme") || "dark"
  );
  const T = THEMES[theme] || THEMES.dark;

  useEffect(() => {
    const root = document.documentElement;

    // ── Inject every token as a CSS variable on :root ──────────────
    // Plain CSS/SCSS can use var(--ats-bg), var(--ats-card), etc.
    Object.entries(T).forEach(([key, val]) => {
      root.style.setProperty(`--ats-${key.replace(/_/g, "-")}`, val);
    });

    // Body background
    document.body.style.backgroundColor = T.bg;
    document.body.style.color           = T.text;
    document.body.style.transition      = "background-color 0.25s, color 0.25s";
  }, [T]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("ats_theme", next);
  };

  const muiTheme = buildMuiTheme(T);

  return (
    <ThemeContext.Provider value={{ theme, T, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// ── Drop-in toggle button ──────────────────────────────────────────
export function ThemeToggle() {
  const { theme, T, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
      style={{
        padding:     "6px 14px",
        borderRadius: 8,
        cursor:       "pointer",
        border:      `1px solid ${T.border}`,
        background:   T.toggle_bg,
        color:        T.muted,
        fontFamily:  "Outfit, sans-serif",
        fontSize:     12,
        fontWeight:   700,
        display:     "flex",
        alignItems:  "center",
        gap:          6,
        transition:  "all 0.2s",
        whiteSpace:  "nowrap",
      }}
    >
      {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
    </button>
  );
}